import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSchool } from "../context/SchoolContext";

/** Close when clicking outside while `when` is true. */
function useOutsideClose(ref, onClose, when) {
  useEffect(() => {
    if (!when) return;

    function onPointerDown(e) {
      if (!ref.current || ref.current.contains(e.target)) return;
      onClose();
    }

    document.addEventListener("pointerdown", onPointerDown);

    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [ref, onClose, when]);
}

/**
 * Dropdown that closes only on true blur, ESC, or outside click.
 * When clicking a Link, close after navigation using a micro-delay.
 */
function NavDropdown({ label, items, align = "left" }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const closeAfterTick = useCallback(() => {
    setTimeout(() => setOpen(false), 0);
  }, []);

  const handleBlurCapture = useCallback(() => {
    setTimeout(() => {
      const el = document.activeElement;

      if (!wrapRef.current?.contains(el)) {
        setOpen(false);
      }
    }, 0);
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useOutsideClose(wrapRef, () => setOpen(false), open);

  if (!items.length) {
    return null;
  }

  return (
    <div
      ref={wrapRef}
      className="relative"
      onBlurCapture={handleBlurCapture}
    >
      <button
        type="button"
        className="px-3 py-2 rounded hover:bg-gray-700"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {label} <span aria-hidden>▾</span>
      </button>

      {open && (
        <ul
          role="menu"
          className={`absolute mt-2 min-w-[12rem] bg-white dark:bg-gray-800 text-black dark:text-white rounded shadow-lg z-50 ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          {items.map((it) => (
            <li key={it.to} role="none">
              <Link
                to={it.to}
                role="menuitem"
                tabIndex={0}
                className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={closeAfterTick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    closeAfterTick();
                  }
                }}
              >
                {it.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** School picker with same close rules. */
function SchoolPicker() {
  const { user } = useAuth();
  const { currentSchoolId, setCurrentSchoolId } = useSchool();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const closeAfterTick = useCallback(() => {
    setTimeout(() => setOpen(false), 0);
  }, []);

  const handleBlurCapture = useCallback(() => {
    setTimeout(() => {
      const el = document.activeElement;

      if (!wrapRef.current?.contains(el)) {
        setOpen(false);
      }
    }, 0);
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useOutsideClose(wrapRef, () => setOpen(false), open);

  const schools = user?.schools || [];

  if (schools.length <= 1) {
    return null;
  }

  const currentName =
    schools.find((s) => s.id === currentSchoolId)?.name || "Select School";

  return (
    <div
      ref={wrapRef}
      className="relative"
      onBlurCapture={handleBlurCapture}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="bg-gray-700 px-3 py-2 rounded hover:bg-gray-600"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {currentName} <span aria-hidden>▾</span>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 text-black dark:text-white rounded shadow-lg z-50"
        >
          {schools.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                role="option"
                aria-selected={currentSchoolId === s.id}
                className={`w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  currentSchoolId === s.id ? "font-semibold" : ""
                }`}
                onClick={() => {
                  setCurrentSchoolId(Number(s.id));
                  closeAfterTick();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    setCurrentSchoolId(Number(s.id));
                    closeAfterTick();
                  }
                }}
              >
                {s.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const [theme, setTheme] = useState(
      () => localStorage.getItem("nightjar_theme") || "system"
    );

  const changeTheme = (value) => {
    window.setNightjarTheme(value);
    setTheme(value);
  };

  const closeAfterTick = useCallback(() => {
    setTimeout(() => setOpen(false), 0);
  }, []);

  const handleBlurCapture = useCallback(() => {
    setTimeout(() => {
      const el = document.activeElement;

      if (!wrapRef.current?.contains(el)) {
        setOpen(false);
      }
    }, 0);
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useOutsideClose(wrapRef, () => setOpen(false), open);

  return (
    <div
      ref={wrapRef}
      className="relative"
      onBlurCapture={handleBlurCapture}
    >
      <button
        type="button"
        className="px-3 py-2 rounded hover:bg-gray-700"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {user.name || user.email} <span aria-hidden>▾</span>
      </button>

    {open && (
      <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 text-black dark:text-white rounded shadow-lg z-50">

        {/* Appearance selector */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <label
            htmlFor="nightjar-theme"
            className="block text-sm font-semibold mb-2"
          >
            Appearance
          </label>

          <select
            id="nightjar-theme"
            value={theme}
            onChange={(e) => changeTheme(e.target.value)}
            className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-2 text-sm"
          >
            <option value="light">☀️ Light</option>
            <option value="dark">🌙 Dark</option>
            <option value="system">💻 System default</option>
          </select>
        </div>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              onLogout();
              closeAfterTick();
            }}
            className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const canAccessHelpdesk = hasPermission("tickets.view");
  const canAccessArborDashboards = hasPermission("arbor.dashboard.view");

  const adminItems = [
    hasPermission("helpdesk.settings")
      ? { label: "Ticket Types", to: "/helpdesk/settings/ticket-types" }
      : null,
    hasPermission("helpdesk.sla.admin")
      ? { label: "SLA Administration", to: "/helpdesk/settings/slas" }
      : null,

    hasPermission("helpdesk.settings")
      ? { label: "Departments", to: "/admin/departments" }
      : null,

    hasPermission("helpdesk.settings")
      ? { label: "Statuses", to: "/admin/statuses" }
      : null,

    hasPermission("helpdesk.settings")
      ? { label: "Categories", to: "/admin/categories" }
      : null,

    hasPermission("staff.manage")
      ? { label: "Users", to: "/admin/users" }
      : null,

    hasPermission("rbac.manage")
      ? { label: "Roles", to: "/admin/roles" }
      : null,

    hasPermission("access.dev-notes")
      ? { label: "Dev Notes", to: "/dev-notes" }
      : null,

    hasPermission("helpdesk.automations.manage")
      ? { label: "Rules", to: "/helpdesk/settings/rules" }
      : null,

    hasPermission("helpdesk.settings")
      ? {
          label: "Notifications",
          to: "/helpdesk/settings/notifications",
        }
      : null,

    hasPermission("helpdesk.settings")
      ? { label: "Branding", to: "/helpdesk/settings/branding" }
      : null,

    hasPermission("manage.kb")
      ? { label: "Knowledge Base", to: "/helpdesk/kb" }
      : null,

    hasPermission("admin.schools.manage")
      ? { label: "Schools", to: "/admin/schools" }
      : null,

    hasPermission("approve.leave")
      ? { label: "Leave Requests", to: "/leave/admin" }
      : null,

    hasPermission("approve.leave")
      ? {
          label: "Leave Allowances",
          to: "/leave/admin/allowances",
        }
      : null,
  ].filter(Boolean);

  return (
    <nav className="flex justify-between items-center bg-gray-800 p-4 text-white">
      {/* Left: brand + main nav */}
      <div className="flex items-center gap-6">
        <Link
          to="/"
          className="font-bold text-lg hover:text-gray-300"
        >
          Nightjar
        </Link>

        {/* Helpdesk */}
        {canAccessHelpdesk && (
          <NavDropdown
            label="Helpdesk"
            items={[
              {
                label: "Dashboard",
                to: "/helpdesk/dashboard",
              },
              {
                label: "View Tickets",
                to: "/helpdesk/tickets",
              },
              {
                label: "New Ticket",
                to: "/helpdesk/tickets/new",
              },
              {
                label: "Problems",
                to: "/helpdesk/problems",
              },
            ]}
          />
        )}

        {/* Knowledge Base */}
        <Link
          to="/helpdesk/kb/browse"
          className="hover:text-gray-300"
        >
          Knowledge Base
        </Link>

        {/* Arbor Dashboards */}
        {canAccessArborDashboards && (
          <Link
            to="/arbor/dashboard"
            className="hover:text-gray-300"
          >
            Arbor Dashboards
          </Link>
        )}

        {/* Admin */}
        <NavDropdown
          label="Admin"
          items={adminItems}
        />
      </div>

      {/* Right: school picker + user menu */}
      <div className="flex items-center gap-4">
        <SchoolPicker />

        {user && (
          <UserMenu
            user={user}
            onLogout={handleLogout}
          />
        )}
      </div>
    </nav>
  );
}